import { Stack, Typography, FormLabel, TooltipProps, Divider } from '@mui/material'
import { C1Tooltip } from 'c1ui'
import { C1LinkButton } from 'components/core/button/C1LinkButton'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1MultiUserDisplay } from 'components/core/multi-user/C1MultiUserDisplay'
import { C1TextWithTruncation } from 'components/core/TextWithTruncation/C1TextWithTruncation'
import { AppValue } from 'lib/actions/apps'
import { searchAppOwnersApi } from 'lib/actions/apps/app-owners'
import { UserValue } from 'lib/actions/user'
import { routes } from 'lib/routes'
import { ExternalLink } from 'lucide-react'
import { useRouter } from 'next/router'
import { SearchAppOwnersRequest } from 'pbts/c1/api/app/v1/app_owner'
import { FormattedMessage } from 'react-intl'

type AppDisplayNameTooltipContentProps = {
    app: AppValue
    parentApp?: AppValue
}

const AppDisplayNameTooltipContent: React.FC<AppDisplayNameTooltipContentProps> = ({ app, parentApp }) => {
    const router = useRouter()
    if (!app) {
        return null
    }

    const { data: appOwnerData, error } =
        searchAppOwnersApi.useSearch(
            SearchAppOwnersRequest.create({ appId: app.id }),
            { actionable: !!app.id, notify: true, autoReload: true, disableEssentialTableExistenceCheck: true },
        )
    const appOwnersList = appOwnerData[0] as UserValue[] || []

    const onAppDetailsPage = router.pathname.startsWith('/admin/applications/[app_id]')

    return <Stack direction="column" gap={1} py={1}>
        {error && <MinimalAPIError
            err={
                <FormattedMessage
                    id="app_display_name_tooltip_error_owners"
                    defaultMessage="Error: Searching app owners"
                />
            }
        />}

        <Stack direction="row" gap={2} mb={1}>
            <Typography variant="h4">{app.displayName}</Typography>
        </Stack>

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.3)' }} />

        {app.appAccountName && (
            <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel sx={{ color: 'inherit' }}>
                    <FormattedMessage id="app_details_app_account_name_label" defaultMessage="Account name:" />
                </FormLabel>
                <C1TextWithTruncation text={app.appAccountName} />
            </Stack>
        )}

        {app.appAccountId && (
            <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel sx={{ color: 'inherit' }}>
                    <FormattedMessage id="app_details_app_account_id_label" defaultMessage="Account ID:" />
                </FormLabel>
                <C1TextWithTruncation text={app.appAccountId} />
            </Stack>
        )}

        {parentApp?.displayName && (
            <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel sx={{ color: 'inherit' }}>
                    <FormattedMessage id="app_details_app_idp_label" defaultMessage="IdP:" />
                </FormLabel>
                <C1TextWithTruncation text={parentApp.displayName} />
            </Stack>
        )}
        {app.description && (
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormLabel sx={{ color: 'inherit' }}>
                    <FormattedMessage id="app_details_app_description_label" defaultMessage="Description:" />
                </FormLabel>
                <Typography>{app.description}</Typography>
            </Stack>
        )}

        {appOwnersList && appOwnersList.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
                <FormLabel sx={{ color: 'inherit' }}>
                    <FormattedMessage id="app_details_app_owners_label" defaultMessage="Owners:" />
                </FormLabel>
                <C1MultiUserDisplay users={appOwnersList} />
            </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
            <FormLabel sx={{ color: 'inherit' }}>
                <FormattedMessage id="app_details_app_id_label" defaultMessage="App ID:" />
            </FormLabel>
            <Stack direction="row" spacing={1} alignItems="center">
                <C1CopyText value={app.id} typographyVariant="monospace" truncateWidth="12em" />
            </Stack>
        </Stack>

        {!onAppDetailsPage && (
            <>
                <Divider sx={{ mb: 1, mt: 2, borderColor: 'rgba(255,255,255,0.3)' }} />

                <C1LinkButton
                    href={routes.applications.detail(app.id)}
                    target="_blank"
                    color="info"
                    variant="text"
                    endIcon={<C1Icon icon={ExternalLink} />}
                >
                    <FormattedMessage id="component_app_tooltip_view_app_details" defaultMessage="View app detail page" />
                </C1LinkButton>
            </>
        )}
    </Stack>
}

type AppDisplayNameTooltipProps = {
    app: AppValue
    parentApp?: AppValue
    children: React.ReactElement
} & Pick<TooltipProps, 'disableHoverListener'>

export const AppDisplayNameTooltip: React.FC<AppDisplayNameTooltipProps> = ({ app, parentApp, children, ...extraProps }) => {
    return <C1Tooltip title={<AppDisplayNameTooltipContent app={app} parentApp={parentApp} />} {...extraProps}>
        {children}
    </C1Tooltip>
}
